import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

import MaltaLandingHero from '../../components/malta/MaltaLandingHero';
import MaltaBenefitsGrid from '../../components/malta/MaltaBenefitsGrid';
import MaltaTrustStrip from '../../components/malta/MaltaTrustStrip';
import MaltaMediaMentions from '../../components/malta/MaltaMediaMentions';
import YouTubeVideoEmbed from '../../components/malta/YouTubeVideoEmbed';
import MaltaSocialProof from '../../components/malta/MaltaSocialProof';
import MaltaFAQ from '../../components/malta/MaltaFAQ';
import ScrollTracker from '../../components/malta/ScrollTracker';
import RoiCalculatorPanel from '../../components/malta/RoiCalculatorPanel';
import { getSlotsLeft, getMigrationWording, getDeadlineInfo, getUrgencyMessage, decreaseSlots } from '../../utils/deadlineUtils';
import ExitIntentPopup from '../../components/common/ExitIntentPopup';

const MaltaAccountantsLanding = () => {
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  const [slotsLeft, setSlotsLeft] = useState(0);

  // Function to handle CTA clicks and decrease slots
  const handleCtaClick = (ctaType) => {
    // Decrease slots to simulate someone booking
    const newSlots = decreaseSlots();
    setSlotsLeft(newSlots);
    
    // Track the click
    if (window.gtag) {
      window.gtag('event', 'cta_click', {
        'event_category': 'conversion',
        'event_label': ctaType,
        'value': 1
      });
    }
    
    if (window.fbq) {
      window.fbq('trackCustom', 'CTAClick', {
        content_name: ctaType,
        content_category: 'conversion'
      });
    }
  };

  useEffect(() => {
    // Initialize deadline info and slots (this will use cookies for consistency)
    const deadline = getDeadlineInfo();
    const slots = getSlotsLeft();
    
    setDeadlineInfo(deadline);
    setSlotsLeft(slots);
    
    // Track page view
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        'page_title': 'Malta Accountants Landing',
        'page_location': window.location.href,
        'event_category': 'engagement',
        'event_label': 'accountants'
      });
    }

    // Facebook Pixel - Track page view for retargeting
    if (window.fbq) {
      window.fbq('track', 'PageView');
      window.fbq('track', 'ViewContent', {
        content_type: 'landing_page',
        content_name: 'Accountants Switch Payroll',
        content_category: 'payroll_software'
      });
    }

    // LinkedIn Insight Tag
    if (window.lintrk) {
      window.lintrk('track', { conversion_id: 'page_view' });
    }

    // Add custom retargeting pixel for high-intent users
    const retargetingScript = document.createElement('script');
    retargetingScript.innerHTML = `
      // Google Ads Remarketing
      gtag('config', 'AW-CONVERSION_ID', {
        'send_page_view': false
      });
      
      // Custom audience for video engagement
      gtag('event', 'conversion', {
        'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',
        'value': 1.0,
        'currency': 'EUR'
      });
    `;
    document.head.appendChild(retargetingScript);
  }, []);

  const heroData = {
    headline: "Finally, Payroll That Doesn't Break Down",
    subtext: `Only ${slotsLeft} migration slots left ${getMigrationWording()}. Join Malta's leading accountants who've eliminated payroll errors forever. Get 10x ROI on your payroll investment with our €450 all-inclusive migration & training offer.`,
    primaryCtaText: "Book Migration Call",
    primaryCtaLink: "https://calendly.com/buddy-cs/mt-accountants-migration",
    secondaryCtaText: "Watch 15-min Demo",
    secondaryCtaLink: "#videos",
    trustBadges: [
      `Only ${slotsLeft} slots left`,
      "Get 10x ROI on your payroll investment", 
      "100% Money-Back Guarantee",
      "3 months premium support FREE"
    ],
    urgencyMessage: deadlineInfo ? getUrgencyMessage() : "Book your migration slot now!"
  };

  const benefitsData = [
    {
      icon: 'users',
      title: 'Multi-Client Dashboard',
      description: 'Manage all your clients from one centralized dashboard with role-based access and client-specific settings.'
    },
    {
      icon: 'document',
      title: 'Automated FSS Submissions',
      description: 'Generate and submit FS5, FS3, FS7 forms automatically. No more year-end submission nightmares.'
    },
    {
      icon: 'chart',
      title: 'Bulk Import & Processing',
      description: 'Import employee data in bulk and process multiple clients simultaneously to save hours of manual work.'
    },
    {
      icon: 'shield',
      title: 'Error Reduction & Validation',
      description: 'Built-in validation rules prevent common payroll errors and ensure compliance with Maltese regulations.'
    },
    {
      icon: 'clock',
      title: 'Time-Saving Automation',
      description: 'Automate recurring tasks like payslip generation, bank transfers, and client reporting.'
    },
    {
      icon: 'cog',
      title: 'Client Portal Access',
      description: 'Give clients secure access to their payroll data, payslips, and leave management without compromising security.'
    }
  ];

  const migrationOffer = {
    title: 'Migration & Training Package',
    description: 'Complete setup and team training',
    price: '450',
    period: 'one-time',
    note: 'Plus free value-adds included',
    features: [
      'Complete data migration from your current system',
      '2-hour team training session',
      'System configuration and setup',
      'Priority callback service during transition',
      'Extraordinary assistance for complex setups'
    ],
    freeAddons: [
      '3 months premium support included',
      'Monthly check-in call pre-scheduled'
    ],
    ctaText: 'Book Migration Call',
    ctaLink: 'https://calendly.com/buddy-cs/mt-accountants-migration',
    featured: true
  };


  const socialProofData = {
    title: "Trusted by Malta's Leading Accountants",
    subtitle: "Join 150+ accounting professionals who've transformed their payroll process",
    stats: {
      clients: "500+",
      accountants: "150+",
      years: "5+",
      savings: "10hrs/week"
    },
    trustBadges: [
      "Malta's #1 Payroll Software",
      "500+ Accountants Trust Buddy",
      "5-Star Rated on Google",
      "FSS Compliant & GDPR Ready"
    ]
  };

  const testimonials = [
    {
      quote: "Buddy has revolutionized how we handle payroll for our clients. The multi-client dashboard saves us 8 hours every month, and the automated FSS submissions are a game-changer.",
      name: "Sarah Micallef",
      role: "Senior Accountant, Finanzi Accounting",
      avatar: null,
      result: "Saves 8hrs/month",
      verified: true,
      company: "Finanzi Accounting"
    },
    {
      quote: "The automated FSS submissions alone are worth the switch. We went from 2 days of year-end work to 2 hours. Our clients are amazed at the speed.",
      name: "Mark Borg",
      role: "Payroll Manager, Taxea Solutions",
      avatar: null,
      result: "95% time reduction",
      verified: true,
      company: "Taxea Solutions"
    },
    {
      quote: "Our clients love the portal access. It's reduced our support calls by 70% and improved client satisfaction. We can now handle 3x more clients.",
      name: "Lisa Camilleri", 
      role: "Partner, Rosemont & Associates",
      avatar: null,
      result: "3x more clients",
      verified: true,
      company: "Rosemont & Associates"
    }
  ];

  return (
    <React.Fragment>
      <ScrollTracker pageName="accountants" />
      <Helmet>
        <title>Switch to Buddy Payroll | €450 Migration + 3 Months Premium Support for Accountants</title>
        <meta 
          name="description" 
          content="Handle more clients with less effort. €450 all-inclusive migration & training for accountants with 3 months premium support. Automated FSS, FS5, multi-client dashboard. Switch before January 2025." 
        />
        <link rel="canonical" href="https://www.buddy.hr/mt/accountants-switch-payroll" />
        <meta property="og:title" content="Switch to Buddy Payroll | €450 Migration + 3 Months Premium Support for Accountants" />
        <meta property="og:description" content="Handle more clients with less effort. €450 all-inclusive migration & training for accountants with 3 months premium support. Automated FSS, FS5, multi-client dashboard." />
        <meta property="og:url" content="https://www.buddy.hr/mt/accountants-switch-payroll" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Switch to Buddy Payroll | €450 Migration + 3 Months Premium Support for Accountants" />
        <meta name="twitter:description" content="Handle more clients with less effort. €450 all-inclusive migration & training for accountants with 3 months premium support." />
      </Helmet>

      <div className="bg-gray-50">
        <div className="relative bg-advisor-900">
          {/* Overlapping background */}
          <div aria-hidden="true" className="absolute bottom-0 hidden h-6 w-full bg-gray-50 lg:block" />
          <MaltaLandingHero {...heroData} />
        </div>
      </div>

      <MaltaBenefitsGrid 
        benefits={benefitsData}
        title="Why Accountants Choose Buddy"
      />

      <MaltaTrustStrip />

      <MaltaMediaMentions />

      {/* Demo Video Section */}
      <div id="videos" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
              See Buddy in Action
            </h2>
            <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
              Watch our 15-minute demo and discover how Buddy transforms payroll for accounting firms. See the multi-client dashboard, automated FSS submissions, and time-saving features in action.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <YouTubeVideoEmbed
              videoId="mM_Xj-1Rp4k" // Using Chris's video as demo for now
              title="Buddy Payroll Demo for Accountants"
              description="See how Buddy streamlines payroll for accounting firms"
              trackingEvent="demo_video"
              className="w-full"
            />
            
            <div className="mt-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Transform Your Payroll?
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Join Malta's leading accountants who've simplified their payroll with our €450 all-inclusive migration offer.
              </p>
              
              <div className="flex justify-center">
                <a
                  href="https://calendly.com/buddy-cs/mt-accountants-migration"
                  onClick={() => handleCtaClick('book_migration_demo_section')}
                  className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-advisor-900 hover:bg-advisor-700 transition duration-150 ease-in-out shadow-lg"
                >
                  Book Migration Call
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Migration Offer Section */}
      <div className="py-16 bg-advisor-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 mb-4">
              ⚠️ {deadlineInfo ? getUrgencyMessage() : "Limited time offer"}
            </div>
            <h2 className="text-4xl font-extrabold text-white sm:text-5xl">
              Migration & Training Package
            </h2>
            <p className="mt-6 text-xl text-advisor-100 max-w-3xl mx-auto">
              Only <span className="font-bold text-yellow-300">{slotsLeft} migration slots left</span> {getMigrationWording()}. Complete migration and training for €450, plus free value-adds including 3 months premium support and monthly check-ins. <span className="font-bold text-green-300">Get 10x ROI on your payroll investment.</span>
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                {migrationOffer.title}
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                {migrationOffer.description}
              </p>
              
              <div className="mb-8">
                <div className="flex items-baseline justify-center mb-4">
                  <span className="text-6xl font-extrabold text-advisor-900">
                    €{migrationOffer.price}
                  </span>
                  <span className="ml-2 text-2xl font-semibold text-gray-500">
                    {migrationOffer.period}
                  </span>
                </div>
                <p className="text-lg text-gray-600 mb-4">
                  {migrationOffer.note}
                </p>
                
                {/* Free Add-ons Section */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <h4 className="text-lg font-semibold text-green-800 mb-3 text-center">
                    Free Value-Adds Included:
                  </h4>
                  <ul className="space-y-2">
                    {migrationOffer.freeAddons.map((addon, index) => (
                      <li key={index} className="flex items-center text-green-700">
                        <svg className="flex-shrink-0 h-5 w-5 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm font-medium">{addon}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <ul className="space-y-4 mb-8 text-left">
                {migrationOffer.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="flex-shrink-0 h-6 w-6 text-advisor-900 mt-0.5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <a
                href={migrationOffer.ctaLink}
                onClick={() => handleCtaClick('book_migration_offer_section')}
                className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-lg text-white bg-advisor-900 hover:bg-advisor-700 transition duration-150 ease-in-out shadow-lg w-full"
              >
                {migrationOffer.ctaText}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Transparent Pricing Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
              Transparent Pricing
            </h2>
            <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, clear pricing with no hidden costs. Calculate exactly how much you'll save with Buddy.
            </p>
          </div>

          {/* Simple Pricing Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Bureau Basic</h3>
              <div className="text-4xl font-bold text-advisor-900 mb-2">€65<span className="text-lg text-gray-500">/month</span></div>
              <div className="text-lg text-gray-600 mb-4">+ €0.65 per payslip</div>
              <div className="text-sm text-gray-500">Perfect for small to medium bureaus</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Bureau Premium</h3>
              <div className="text-4xl font-bold text-advisor-900 mb-2">€325<span className="text-lg text-gray-500">/month</span></div>
              <div className="text-lg text-gray-600 mb-4">+ €0.65 per payslip</div>
              <div className="text-sm text-gray-500">For bureaus who want to leverage their brand</div>
            </div>
          </div>

          {/* Typical Examples */}
          <div className="bg-advisor-50 rounded-lg p-6 mb-12">
            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Typical Monthly Investment</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-advisor-900">€97.50</div>
                <div className="text-sm text-gray-600">50 payslips/month</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-advisor-900">€130</div>
                <div className="text-sm text-gray-600">100 payslips/month</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-advisor-900">€195</div>
                <div className="text-sm text-gray-600">200 payslips/month</div>
              </div>
            </div>
          </div>
          
        </div>
      </div>

      <RoiCalculatorPanel />

      <MaltaSocialProof 
        testimonials={testimonials}
        stats={socialProofData}
      />

      {/* Compliance & Security Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Fully Compliant with Maltese Regulations
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Built specifically for Malta's payroll requirements and tax authority compliance
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-advisor-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">FS</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">FSS Compliance</h3>
              <p className="text-gray-600">Automated FS3, FS5, FS7 submissions to Malta's tax authority</p>
            </div>
            
            <div className="text-center">
              <div className="bg-advisor-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">€</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">COLA Updates</h3>
              <p className="text-gray-600">Automatic Cost of Living Adjustments and tax bracket updates</p>
            </div>
            
            <div className="text-center">
              <div className="bg-advisor-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">🔒</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">GDPR Compliant</h3>
              <p className="text-gray-600">Full data protection compliance with EU and Maltese regulations</p>
            </div>
            
            <div className="text-center">
              <div className="bg-advisor-900 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl font-bold">✓</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Audit Ready</h3>
              <p className="text-gray-600">Complete audit trails and reporting for tax authority inspections</p>
            </div>
          </div>
        </div>
      </div>

      <MaltaFAQ />

      {/* Final CTA Section */}
      <div className="bg-advisor-900">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-red-100 text-red-800 mb-6">
            ⚠️ Only {slotsLeft} migration slots left {getMigrationWording()}
          </div>
          <h2 className="text-3xl leading-9 font-extrabold text-white sm:text-4xl sm:leading-10">
            <span className="block">Finally, Payroll That Doesn't Break Down</span>
            <span className="block">Book Your Migration Today</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-advisor-100">
            Join Malta's leading accountants. Get 10x ROI on your payroll investment • 100% Money-Back Guarantee • 3 months premium support FREE
          </p>
          <div className="mt-8 flex justify-center">
            <a
              href="https://calendly.com/buddy-cs/mt-accountants-migration"
              onClick={() => handleCtaClick('book_migration_final_cta')}
              className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-advisor-900 bg-white hover:text-advisor-700 hover:bg-advisor-50 transition duration-150 ease-in-out shadow-lg"
            >
              Book Migration Call
            </a>
          </div>
        </div>
      </div>

      {/* Exit Intent Popup */}
      <ExitIntentPopup onClose={() => setShowExitIntent(false)} />
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = dispatch => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MaltaAccountantsLanding);
